import { NO, UITextView, UIView } from "uicore-ts"
import { formatRHDateValue } from "../../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHColors, RHFont, RHRadius, RHShadow, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import {
    RHCPVSummationInputPart,
    RHCPVSummationLevelResult,
    RHCPVSummationReport,
    RHCPVSummationReportRequest,
    RHCPVSummationStatus
} from "../../SocketClientFunctions"
import { CPVSummationDetailsView } from "./CPVSummationDetailsView"


interface CPVSummationReportPartGroup {
    part: RHCPVSummationInputPart
    levels: RHCPVSummationLevelResult[]
}


export class CPVSummationReportView extends UIView {
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Hankelepingute summeerimine",
            textColor: RHColors.textPrimary
        })
        .addedAsSubviewToView(this)
    
    readonly summaryLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            textColor: RHColors.slateDark,
            style: {
                fontSize: RHFont.sizeSmall,
                lineHeight: "1.35"
            }
        })
        .addedAsSubviewToView(this)
    
    readonly bannerLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            textColor: RHColors.textPrimary,
            style: {
                fontSize: RHFont.sizeSmall,
                fontWeight: RHFont.weightSemibold,
                lineHeight: "1.35"
            }
        })
        .addedAsSubviewToView(this)
    
    readonly sectionsContainerView = new UIView()
        .configuredWithObject({
            layoutSubviews: () => {
                this.sectionsContainerView.contentBoundsWithInset(0)
                    .framesByDistributingViewsAsColumn(
                        this._partSectionViews,
                        RHSpacing.medium
                    )
            }
        })
        .addedAsSubviewToView(this)
    
    _partSectionViews: CPVSummationDetailsView[] = []
    
    constructor(elementID?: string) {
        super(elementID)
        
        ;(this as CPVSummationReportView).configureWithObject({
            backgroundColor: RHColors.whiteAlpha98,
            style: {
                borderRadius: `${RHRadius.large}px`,
                border: `1px solid ${RHColors.borderSlate.stringValue}`,
                boxShadow: RHShadow.subtle
            }
        })
    }
    
    updateWithReport(
        report: RHCPVSummationReport | undefined,
        request?: RHCPVSummationReportRequest
    ) {
        this._clearPartSections()
        
        if (!report) {
            this.summaryLabel.configureWithObject({
                text: "Vali CPV-kood ja sisesta maksumus, et näha viimase 12 kuu summeerimist."
            })
            this.bannerLabel.configureWithObject({
                text: ""
            })
            this.sectionsContainerView.removeFromSuperview()
            this._applyBannerStyle("ok")
            this.setNeedsLayoutUpToRootView()
            return
        }
        
        this.sectionsContainerView.addedAsSubviewToView(this)
        this.summaryLabel.configureWithObject({
            text: this._summaryTextForReport(report)
        })
        this.bannerLabel.configureWithObject({
            text: this._bannerTextForReport(report)
        })
        this._applyBannerStyle(report.status)
        const groups = this._groupsForReportAndRequest(report, request)
        const levelsByCPV = new Map<string, RHCPVSummationLevelResult>()
        for (const group of groups) {
            for (const level of group.levels) {
                if (level.status === "red") {
                    levelsByCPV.set(level.cpvAtLevel, level)
                }
            }
        }
        this._partSectionViews = [...levelsByCPV.values()].map(level => {
            const sectionView = new CPVSummationDetailsView()
            sectionView.updateWithReportForLevel(report, level)
            sectionView.addedAsSubviewToView(this.sectionsContainerView)
            return sectionView
        })
        this.sectionsContainerView.setNeedsLayout()
        this.setNeedsLayoutUpToRootView()
    }
    
    _clearPartSections() {
        this._partSectionViews.forEach(sectionView => sectionView.removeFromSuperview())
        this._partSectionViews = []
    }
    
    _groupsForReportAndRequest(
        report: RHCPVSummationReport,
        request?: RHCPVSummationReportRequest
    ): CPVSummationReportPartGroup[] {
        const groups: CPVSummationReportPartGroup[] = []
        if (request) {
            for (const part of request.parts) {
                const levels = CPVSummationDetailsView.levelsForPartInReport(part, report)
                if (levels.length > 0) {
                    groups.push({
                        part,
                        levels
                    })
                }
            }
        }
        if (groups.length > 0) {
            return groups
        }
        return [
            {
                part: { cpv9: "" },
                levels: report.levels
            }
        ]
    }
    
    _summaryTextForReport(report: RHCPVSummationReport): string {
        const referenceDate = formatRHDateValue(report.referenceDate, "-")
        const windowStart = formatRHDateValue(report.windowStart, "-")
        const windowEnd = formatRHDateValue(report.windowEnd, "-")
        return `Võrdluskuupäev ${referenceDate}; arvestusperiood ${windowStart} kuni ${windowEnd}.`
    }
    
    _bannerTextForReport(report: RHCPVSummationReport): string {
        if (report.explanation) {
            return report.explanation
        }
        if (report.status === "red") {
            return "Hoiatus: CPV-kogusumma ületab piirmäära ja vajab kooskõlastust."
        }
        if (report.status === "yellow") {
            return "Tähelepanu: CPV-kogusumma läheneb piirmäärale."
        }
        return "CPV-kogusumma jääb piirmäära piiresse."
    }
    
    _applyBannerStyle(status: RHCPVSummationStatus) {
        let backgroundColor = RHColors.successGreenSurfaceFaint
        let borderColor = RHColors.greenBorderAlpha34
        if (status === "red") {
            backgroundColor = RHColors.pinkGlass
            borderColor = RHColors.softDangerBorderAlpha30
        }
        if (status === "yellow") {
            backgroundColor = RHColors.warningAmberSurfaceDraft
            borderColor = RHColors.warningAmberBorderDraft
        }
        this.bannerLabel.configureWithObject({
            backgroundColor,
            style: {
                border: `1px solid ${borderColor.stringValue}`,
                borderRadius: `${RHRadius.medium}px`
            }
        })
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const bounds = this.contentBoundsWithInset(RHSpacing.medium)
        const bannerHorizontalInset = RHSpacing.small
        
        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHSpacing.small, this.summaryLabel)
            .assignedAsFrameOfView(this.summaryLabel)
            .IF(this.bannerLabel.text.length > 0)
            .rectangleForNextRow(RHSpacing.small, this.bannerLabel.intrinsicContentHeight(
                bounds.width - bannerHorizontalInset * 2
            ) + RHSpacing.small)
            .rectangleWithInsets(bannerHorizontalInset, bannerHorizontalInset, 0, 0)
            .assignedAsFrameOfView(this.bannerLabel)
            .ENDIF()
            .IF(this.sectionsContainerView.superview === this)
            .rectangleForNextRow(RHSpacing.medium, this.sectionsContainerView)
            .assignedAsFrameOfView(this.sectionsContainerView)
            .ENDIF()
    }
    
}
